import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Scales } from '@phosphor-icons/react'
import { Badge, DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { useCrops, useUser } from '../../api/reference'
import { useLotBalances } from '../../api/recall'
import type { LotBalanceRow } from '../../model/recall/mappers'
import { accountedTone, flagLabel, pctText, seasonLabel } from '../../model/recall/lotBalance'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { PersonPicker } from '../../components/PersonPicker'
import { Kg, RefLink, StatTile, UserName } from '../../components/bits'
import { useUrlState } from '../../lib/useUrlState'

const LIMIT = 25

/** Lot balance — F7.9: for every farmer lot, where each kilo went and the share accounted for. */
export function LotBalancePage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({ seasonCode: '', cropId: '', farmerId: '', search: '', flagged: '', page: 1 })
  const crops = useCrops()
  const farmer = useUser(f.farmerId || null)
  const q = useLotBalances({
    page: f.page,
    limit: LIMIT,
    seasonCode: f.seasonCode || undefined,
    cropId: f.cropId || undefined,
    farmerId: f.farmerId || undefined,
    search: f.search || undefined,
    flagged: f.flagged === 'true' ? true : undefined,
  })
  const seasons = q.data?.seasons ?? []
  const cropName = (id: string | null) => (crops.data ?? []).find((c) => c.id === id)?.name ?? null
  const seasonOptions = useMemo(() => {
    const set = new Set<string>()
    for (const s of q.data?.seasons ?? []) set.add(s.seasonCode)
    for (const r of q.data?.rows ?? []) if (r.seasonCode) set.add(r.seasonCode)
    if (f.seasonCode) set.add(f.seasonCode)
    return [...set].sort().reverse().map((s) => ({ value: s, label: seasonLabel(s) }))
  }, [q.data, f.seasonCode])

  const columns: Column<LotBalanceRow>[] = [
    {
      id: 'lot',
      header: 'Lot',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <RefLink to={`/c/lots/${r.lotId}`}>{r.lotCode}</RefLink>
          <span className="text-[12px] text-muted">
            {[cropName(r.cropId), seasonLabel(r.seasonCode)].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    { id: 'farmer', header: 'Farmer', cell: (r) => <UserName id={r.farmerId} />, hideBelow: 'md' },
    { id: 'gate', header: 'Gate net', align: 'right', cell: (r) => <Kg grams={r.gateNetG} /> },
    { id: 'received', header: 'Received', align: 'right', cell: (r) => <Kg grams={r.receivedG} />, hideBelow: 'lg' },
    { id: 'issued', header: 'To machines', align: 'right', cell: (r) => <Kg grams={r.issuedG} />, hideBelow: 'lg' },
    { id: 'bags', header: 'In sacks', align: 'right', cell: (r) => <Kg grams={r.inLotBagsG} />, hideBelow: 'lg' },
    {
      id: 'batches',
      header: 'Batches (brand / ordinary)',
      align: 'right',
      hideBelow: 'lg',
      cell: (r) => (
        <span className="inline-flex flex-col items-end leading-tight">
          <Kg grams={r.brandBatchG} />
          <Kg grams={r.convBatchG} className="text-[12px] text-muted" />
        </span>
      ),
    },
    {
      id: 'byproduct',
      header: 'By-product / waste',
      align: 'right',
      hideBelow: 'lg',
      cell: (r) => (
        <span className="inline-flex flex-col items-end leading-tight">
          <Kg grams={r.byproductG} />
          <Kg grams={r.wasteG} className="text-[12px] text-muted" />
        </span>
      ),
    },
    {
      id: 'packed',
      header: 'Packed',
      hideBelow: 'md',
      cell: (r) => <PackedCell r={r} />,
    },
    {
      id: 'pct',
      header: 'Accounted',
      align: 'right',
      cell: (r) => {
        const tone = accountedTone(r)
        return (
          <span className={tone === 'danger' ? 'font-mono font-semibold text-danger' : tone === 'ok' ? 'font-mono text-ok' : 'font-mono'}>
            {pctText(r.accountedPct)}
          </span>
        )
      },
    },
    { id: 'lost', header: 'Lost', align: 'right', cell: (r) => <Kg grams={r.lostG} className={r.lostG > 0 ? undefined : 'text-muted'} />, hideBelow: 'sm' },
    {
      id: 'flags',
      header: 'Flags',
      cell: (r) =>
        r.flagReasons.length === 0 ? (
          r.openRuns > 0 ? <span className="text-[12.5px] text-muted">Run still open</span> : <span className="text-muted">—</span>
        ) : (
          <span className="flex flex-col gap-1">
            {r.flagReasons.map((fl) => (
              <Badge key={fl} tone="danger">
                {flagLabel(fl, r.accountedMinPct)}
              </Badge>
            ))}
          </span>
        ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Lot balance"
      description="For each farmer’s lot: bought at the gate, sent to the machines, turned into batches, packed, or lost — and the share accounted for."
    >
      {seasons.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {seasons.map((s) => {
            const tone = s.lotsOverPacked > 0 ? 'danger' : s.accountedPct !== null && s.accountedPct < 98 ? 'warn' : 'ok'
            return (
              <StatTile
                key={`${s.seasonCode}-${s.cropId}`}
                label={`${seasonLabel(s.seasonCode)} · ${s.cropName}`}
                value={pctText(s.accountedPct)}
                tone={tone}
                hint={
                  <span className="flex flex-col">
                    <span>
                      {s.lots} {s.lots === 1 ? 'lot' : 'lots'} · <Kg grams={s.gateNetG} className="text-[inherit]" /> at the gate
                    </span>
                    <span>
                      Released <Kg grams={s.packedReleasedG} className="text-[inherit]" /> · held <Kg grams={s.packedHeldG} className="text-[inherit]" />
                    </span>
                    {s.lotsOverPacked > 0 && <span className="text-danger">{s.lotsOverPacked} packed more than delivered</span>}
                  </span>
                }
              />
            )
          })}
        </div>
      )}
      <DataTable<LotBalanceRow>
        caption="Lot balance"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.lotId}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/lot-balance/${r.lotId}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Lot code" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. LOT-0042" />
            <SelectFilter label="Season" value={f.seasonCode} onChange={(seasonCode) => setF({ seasonCode })} options={seasonOptions} />
            <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <SelectFilter<'true'>
              label="Flagged"
              value={f.flagged === 'true' ? 'true' : ''}
              onChange={(v) => setF({ flagged: v })}
              options={[{ value: 'true', label: 'Flagged only' }]}
              allLabel="All lots"
            />
            <div className="flex min-w-[14rem] flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">Farmer</span>
              <PersonPicker
                label="Farmer"
                value={f.farmerId ? (farmer.data ?? { id: f.farmerId, name: '…', username: '', farmerCode: null, contactNo: null, roles: [] }) : null}
                onChange={(u) => setF({ farmerId: u?.id ?? '' })}
                placeholder="Name or roster no."
              />
            </div>
          </FilterBar>
        }
        empty={<EmptyState icon={<Scales />} title="No lots match" description="A lot shows here once it is committed at the gate." />}
      />
    </ConsolePage>
  )
}

function PackedCell({ r }: { r: LotBalanceRow }) {
  const parts: Array<[string, number]> = [
    ['waiting', r.packedWaitingG],
    ['released', r.packedReleasedG],
    ['held', r.packedHeldG],
    ['destroyed', r.packedDestroyedG],
  ]
  const shown = parts.filter(([, g]) => g > 0)
  if (shown.length === 0) return <span className="text-muted">—</span>
  return (
    <span className="flex flex-col text-[12.5px] leading-tight">
      {shown.map(([label, g]) => (
        <span key={label} className="whitespace-nowrap">
          <Kg grams={g} /> <span className="text-muted">{label}</span>
        </span>
      ))}
    </span>
  )
}
